// ─── Centralized navigation config ──────────────────────────────────────────
// Single source of truth for all nav items, views, command palette

export const NAV_SECTIONS = [
  {
    label: 'Create',
    items: [
      { id: 'dashboard',       label: 'Dashboard',       icon: 'dashboard',   view: 'dashboard' },
      { id: 'create',          label: 'Create Episode',  icon: 'play',        view: 'brief',      accent: true },
      { id: 'episodes',        label: 'My Episodes',     icon: 'list',        view: 'episodes',   badgeCount: true },
    ],
  },
  {
    label: 'Grow',
    items: [
      { id: 'analytics',       label: 'Analytics',       icon: 'analytics',   view: 'analytics' },
      { id: 'content-signals', label: 'Content Signals', icon: 'signals',     view: 'analytics',  badge: 'HOT', badgeType: 'hot', notification: true },
    ],
  },
  {
    label: 'Monetize',
    items: [
      { id: 'marketplace',     label: 'Monetization',    icon: 'marketplace', view: 'marketplace', badge: 'New', badgeType: 'new', notification: true },
    ],
  },
  {
    label: 'Account',
    items: [
      { id: 'settings',        label: 'Settings',        icon: 'settings',    view: 'settings' },
      { id: 'billing',         label: 'Billing',         icon: 'billing',     view: 'billing' },
    ],
  },
];

// ── V4 simplified sidebar (Suno-style primary items) ──────────────────────
// Mirrors Suno's pattern — Home / Explore / Search / Create / Profile /
// Library / Inbox. Everything else (Partners, Clips, Episodes, Analytics,
// Mad Science, Pricing, Settings, Billing, Help, Support) lives in More.
//
// (Short-form was a standalone entry here; it now lives as a tab inside
// the chat on /create, so it no longer needs its own sidebar row.)
export const V4_NAV = [
  { id: 'dashboard',    label: 'Home',          icon: 'home',     view: 'dashboard' },
  { id: 'explore',      label: 'Explore',       icon: 'compass',  view: 'explore' },
  { id: 'search',       label: 'Search',        icon: 'search',   view: 'search' },
  { id: 'create',       label: 'Create',        icon: 'plus',     view: 'builder' },
  { id: 'profile',      label: 'Profile',       icon: 'person',   view: 'profile' },
  { id: 'library',      label: 'Library',       icon: 'library',  view: 'episodes' },
  { id: 'inbox',        label: 'Inbox',         icon: 'inbox',    view: 'inbox' },
];

// V4_MORE lists everything below the primary rail.
export const V4_MORE = [
  { id: 'partners',    label: 'Partners',        view: 'partners',
    badge: 'SOON', badgeType: 'soon' },
  { id: 'clips',       label: 'Clips',           view: 'clips' },
  { id: 'episodes',    label: 'My Episodes',     view: 'episodes' },
  { id: 'analytics',   label: 'Analytics',       view: 'analytics' },
  { id: 'labs',        label: 'Mad Science',     view: 'labs' },
  { id: 'subscription',label: 'Subscription',    view: 'subscription' },
  { id: 'pricing',     label: 'Plans & pricing', view: 'pricing' },
  { id: 'settings',    label: 'Settings',        view: 'settings' },
  { id: 'billing',     label: 'Billing',         view: 'billing' },
  { id: 'help',        label: 'Help',            view: 'help' },
  { id: 'support',     label: 'Support',         view: 'support' },
];

// Flat arrays for backwards compatibility
export const PRIMARY_NAV = NAV_SECTIONS.flatMap(s => s.items).filter(i =>
  ['dashboard', 'create', 'episodes'].includes(i.id)
);

export const SYSTEM_NAV = [
  { id: 'settings',  label: 'Settings',  icon: 'settings', view: 'settings' },
  { id: 'billing',   label: 'Billing',   icon: 'billing',  view: 'billing' },
  { id: 'help',      label: 'Help',      icon: 'help',     view: 'help' },
];

export const COMMAND_PALETTE_ITEMS = [
  { id: 'create',      label: 'Create new episode',  icon: 'compose',     view: 'brief',        shortcut: 'N' },
  { id: 'dashboard',   label: 'Go to Dashboard',     icon: 'home',        view: 'dashboard' },
  { id: 'episodes',    label: 'Browse Episodes',     icon: 'library',     view: 'episodes' },
  { id: 'analytics',   label: 'View Analytics',      icon: 'analytics',   view: 'analytics' },
  { id: 'partners',    label: 'Partners (coming soon)', icon: 'handshake', view: 'partners' },
  { id: 'pricing',     label: 'Plans & pricing',     icon: 'billing',     view: 'pricing' },
  { id: 'settings',    label: 'Account Settings',    icon: 'settings',    view: 'settings' },
  { id: 'billing',     label: 'Manage Billing',      icon: 'billing',     view: 'billing' },
  { id: 'help',        label: 'Help & Docs',         icon: 'help',        view: 'help' },
  { id: 'support',     label: 'Contact support',     icon: 'help',        view: 'support' },
  { id: 'admin',       label: 'Admin Dashboard',     icon: 'admin',       view: 'admin',        adminOnly: true },
];

// Views that show the full app shell (sidebar + topnav)
export const SHELL_VIEWS = [
  'dashboard', 'episodes', 'analytics', 'marketplace', 'settings', 'billing',
  'help', 'support', 'admin', 'brief', 'results', 'thinking', 'labs', 'explore',
  'partners', 'pricing', 'clips', 'builder',
  // PR-A: profile + subscription + search are first-class sidebar
  // destinations now (subscription is the closed-beta plans hub; profile
  // is the public-page editor; search is the full search surface).
  'profile', 'subscription', 'search', 'inbox', 'collabs',
];

// Views that map to nav items for active state highlighting
export const VIEW_TO_NAV = {
  dashboard: 'dashboard',
  brief: 'create',
  builder: 'create',
  thinking: 'create',
  // Library (sidebar) is the same view as "My Episodes". Highlight Library
  // in the rail when on the episodes/results view.
  results: 'library',
  episodes: 'library',
  labs: 'labs',
  explore: 'explore',
  search: 'search',
  analytics: 'analytics',
  // 'marketplace' is the legacy alias — both sidebar items route to partners now.
  marketplace: 'partners',
  partners: 'partners',
  pricing: 'pricing',
  clips: 'clips',
  'clip-upload': 'clips',
  settings: 'settings',
  billing: 'billing',
  // Subscription (closed-beta plans page) is its own sidebar destination.
  subscription: 'subscription',
  help: 'help',
  support: 'help',
  admin: 'admin',
  // Profile sidebar item = the user's own /@me page.
  profile: 'profile',
  'guest-profile': 'profile',
  'guest-profile-edit': 'profile',
  account: 'profile',
  // Inbox = host-guest pitches surface.
  inbox:   'inbox',
  matches: 'collabs',
  // Collabs — full pipeline management for guesting collabs.
  collabs: 'collabs',
};
